import { useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import RouteSuspense from './RouteSuspense'
import { useTranslation } from 'react-i18next'
import { useOrgAuth } from '../contexts/OrgAuthContext'
import LanguageSwitcher from './LanguageSwitcher'
import Button from './ui/Button'
import AppHeader from './AppHeader'
import UserMenu from './UserMenu'
import { Z } from '../lib/zIndex'

export default function Layout() {
  const { t } = useTranslation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { session, isLoading } = useOrgAuth()

  const closeMobileMenu = () => setMobileMenuOpen(false)

  const location = useLocation()

  const getHeaderTitle = () => {
    if (location.pathname.startsWith('/procurements')) {
      return t('home.modules.procurements.title', 'Public Procurements')
    }
    return t('header.title', 'Rochd Systems')
  }

  return (

    <div className="min-h-screen flex flex-col bg-paper">
      {session ? (
        <AppHeader title={getHeaderTitle()} />
      ) : (
      <header className={`bg-white border-b border-slate-200 sticky top-0 z-[${Z.header}] pt-safe`}>

        <nav className="max-w-6xl mx-auto px-4 sm:px-6 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 group" onClick={closeMobileMenu}>
              <img
                src="/logo.svg"
                alt="Logo"
                className="w-8 h-8 flex-shrink-0"
              />
              <span className="text-base sm:text-lg font-semibold text-slate-800 group-hover:text-primary-700 transition-colors leading-tight">
                {t('header.title')}
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden sm:flex items-center gap-1">
              <div className="w-px h-6 bg-slate-200 mx-2" />
              <LanguageSwitcher />

              <div className="ms-2 ps-2 border-s border-slate-200 flex items-center h-8">
                {isLoading ? (
                  <div className="w-8 h-8 rounded-full bg-slate-200 animate-pulse" />
                ) : session ? (
                  <UserMenu />
                ) : (
                  <Link
                    to="/login"
                    className="inline-flex items-center rounded-lg bg-brand-600 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-700"
                  >
                    {t('auth.login', 'Log in')}
                  </Link>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex sm:hidden items-center gap-2">
              <div className="me-1 flex items-center">
                {isLoading ? null : session ? <UserMenu /> : null}
              </div>
              <LanguageSwitcher />
              <Button
                variant="ghost"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2"
                aria-label={t('common.toggleMenu')}
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="sm:hidden mt-3 pt-3 border-t border-slate-100">
              <div className="flex flex-col gap-1">
                {!session && !isLoading && (
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="px-4 py-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                  >
                    {t('auth.login', 'Log in')}
                  </Link>
                )}
              </div>
            </div>
          )}
        </nav>
      </header>
      )}

      <main className="flex-1 min-h-0 max-w-[92rem] mx-auto px-3 sm:px-4 lg:px-5 pt-3 sm:pt-4 pb-3 sm:pb-4 w-full">
        <RouteSuspense><Outlet /></RouteSuspense>
      </main>

      <footer className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid gap-8 sm:grid-cols-[1fr_auto_auto] text-sm">
          <div>
            <div className="flex items-center gap-2">
              <img src="/logo.svg" alt="Logo" className="w-7 h-7 flex-shrink-0" />
              <span className="font-semibold text-slate-800">{t('header.title')}</span>
            </div>
            <p className="mt-2 text-slate-500">{t('footer.tagline', '')}</p>
          </div>
          <nav className="flex flex-col gap-2" aria-label="Product">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t('footer.product', '')}</span>
            <Link to="/procurements" className="text-slate-600 hover:text-ink transition-colors">
              {t('footer.procurements', 'Public procurements')}
            </Link>
            <Link to="/login" className="text-slate-600 hover:text-ink transition-colors">
              {t('auth.login', 'Log in')}
            </Link>
            <a href="mailto:contact@rochd.systems?subject=Démonstration" className="text-slate-600 hover:text-ink transition-colors">
              {t('home.contactDemo', 'Request a demo')}
            </a>
          </nav>
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t('footer.contact', '')}</span>
            <a href="mailto:contact@rochd.systems" className="text-slate-600 hover:text-ink transition-colors">
              contact@rochd.systems
            </a>
          </div>
        </div>
        <div className="border-t border-slate-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-center text-xs sm:text-sm text-slate-500">
            &copy; {new Date().getFullYear()} {t('header.title')}. {t('common.allRightsReserved')}
          </div>
        </div>
      </footer>
    </div>
  )
}
